import { useState, useEffect, useMemo } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import styles from './styles.module.scss';
import { Board } from '../../../../../components/Board';
import { verifyRoutePermissions } from '../../../../../globalFunctions';
import { useNavigate } from 'react-router-dom';
import { NavButton } from '../../../../../components/NavButton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import moment from 'moment';

// Cores para gerar linhas dinamicamente
const LINE_COLORS = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#a83232', '#6b32a8'
];

interface ReportRawData {
    answer_id: number;
    created_at: string;
    abstracao: number;
    decomposicao: number;
    padroes: number;
    algoritmos: number;
    fk_user_id: number;
    nome_aluno: string;
    class_id: number;
    turma: string;
}

export const PcReports = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<any>();
    
    // Todos os dados brutos do backend
    const [rawData, setRawData] = useState<ReportRawData[]>([]);
    
    // Lista de turmas únicas para a Sidebar
    const [classList, setClassList] = useState<string[]>([]);
    
    // Turma selecionada
    const [selectedClass, setSelectedClass] = useState<string | null>(null);

    // Termo de pesquisa (agora filtra turmas na lista)
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.classes'],
            navigate
        );
        setUser(user);
    }, []);

    const requestConfig: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${user?.token}`,
            Accept: 'application/json'
        }
    };

    const getReports = async () => {
        if (!user?.token) return;

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/logicmanager/tests/report`,
                requestConfig
            );
            
            const data: ReportRawData[] = response.data;
            setRawData(data);

            // Extrair lista de turmas únicas
            const uniqueClasses = Array.from(new Set(data.map(item => item.turma))).sort();
            setClassList(uniqueClasses);

            // Seleciona a primeira turma por padrão
            if (uniqueClasses.length > 0) {
                setSelectedClass(uniqueClasses[0]);
            }

        } catch (error) {
            console.error("Erro ao buscar relatórios", error);
        }
    };

    useEffect(() => {
        if (user) getReports();
    }, [user]);

    // --- Lógica de Processamento do Gráfico ---
    const chartDataInfo = useMemo(() => {
        if (!selectedClass) return { data: [], students: [] };

        // 1. Filtrar dados apenas da turma selecionada
        const classData = rawData.filter(d => d.turma === selectedClass);

        // 2. Identificar todos os alunos únicos dessa turma (para criar as Linhas)
        const uniqueStudents = Array.from(new Set(classData.map(d => d.nome_aluno)));

        // 3. Transformar dados para o formato do Recharts
        // O Recharts precisa de um array onde cada objeto é um ponto no eixo X (Data)
        // e as chaves são os nomes dos alunos.
        // Ex: { date: '10/10', 'Joao': 8.5, 'Maria': null }
        
        // Agrupar por data formatada
        const groupedByDate: { [date: string]: any } = {};

        classData.forEach(entry => {
            const dateKey = moment(entry.created_at).format('DD/MM/YYYY HH:mm');
            
            // Calcula a média das 4 notas para este teste específico
            const averageGrade = (
                Number(entry.abstracao) + 
                Number(entry.decomposicao) + 
                Number(entry.padroes) + 
                Number(entry.algoritmos)
            ) / 4;

            if (!groupedByDate[dateKey]) {
                groupedByDate[dateKey] = { date: dateKey };
            }

            // Atribui a nota à chave com o nome do aluno
            groupedByDate[dateKey][entry.nome_aluno] = parseFloat(averageGrade.toFixed(2));
        });

        // Converter objeto em array e ordenar por data (se necessário)
        // Nota: Como o backend já ordena por created_at, a ordem de inserção deve estar correta,
        // mas datas strings podem bagunçar se não ordenarmos. O ideal é manter timestamp, mas para simplificar:
        const finalChartData = Object.values(groupedByDate);

        return { data: finalChartData, students: uniqueStudents };
    }, [rawData, selectedClass]);


    // Filtrar turmas na sidebar
    const filteredClasses = classList.filter(c => 
        c.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Casts do Recharts
    const LineChartA = LineChart as any;
    const ResponsiveContainerA = ResponsiveContainer as any;
    const XAxisA = XAxis as any;
    const YAxisA = YAxis as any;
    const LineA = Line as any;
    const LegendA = Legend as any;

    return (
        <div className={styles.wrapper}>
            <NavButton
                route='/manager'
                direction='left'
                style={{ left: '0.4vw', top: '2vw', zIndex: 20 }}
            />
            <Board wrapperClass={styles.board}>
                <div className={styles.dashboardContainer}>
                    
                    {/* --- SIDEBAR: LISTA DE TURMAS --- */}
                    <aside className={styles.sidebar}>
                        <div className={styles.searchBox}>
                            <input 
                                type="text" 
                                placeholder="Pesquisar turma..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                        </div>

                        <h3 className={styles.sidebarTitle}>
                            Turmas ({filteredClasses.length})
                        </h3>
                        
                        <div className={styles.cardList}>
                            {filteredClasses.map((turmaName) => (
                                <div 
                                    key={turmaName}
                                    className={`${styles.studentCard} ${selectedClass === turmaName ? styles.activeCard : ''}`}
                                    onClick={() => setSelectedClass(turmaName)}
                                    style={{ padding: '15px' }} // Ajuste visual simples
                                >
                                    <div className={styles.cardInfo}>
                                        <span className={styles.cardName} style={{fontSize: '1.1rem'}}>{turmaName}</span>
                                    </div>
                                    <div className={styles.gradeBadge} style={{background: '#eee', color: '#333'}}>
                                        Ver
                                    </div>
                                </div>
                            ))}
                        </div>
                    </aside>

                    {/* --- CONTEÚDO PRINCIPAL --- */}
                    <main className={styles.mainContent}>
                        {selectedClass ? (
                            <>
                                <header className={styles.contentHeader}>
                                    <h2>Desempenho: {selectedClass}</h2>
                                    <p>Comparativo de evolução de notas entre os alunos da turma.</p>
                                </header>

                                <div className={styles.chartContainer} style={{height: '500px'}}>
                                    {chartDataInfo.data.length > 0 ? (
                                        <ResponsiveContainerA width="100%" height="100%">
                                            <LineChartA
                                                data={chartDataInfo.data}
                                                margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                                <XAxisA 
                                                    dataKey="date" 
                                                    padding={{ left: 30, right: 30 }}
                                                    tick={{fontSize: 12}}
                                                />
                                                <YAxisA domain={[0, 10]} />
                                                <Tooltip 
                                                    contentStyle={{ borderRadius: '8px' }}
                                                    itemSorter={(item: any) => -item.value} // Ordena tooltip da maior nota para menor
                                                />
                                                <LegendA verticalAlign="top" height={36}/>
                                                
                                                {/* GERA UMA LINHA PARA CADA ALUNO ENCONTRADO NA TURMA */}
                                                {chartDataInfo.students.map((studentName, index) => (
                                                    <LineA
                                                        key={studentName}
                                                        connectNulls // Importante: conecta pontos se o aluno faltou em alguma data intermediária
                                                        type="monotone"
                                                        dataKey={studentName}
                                                        name={studentName}
                                                        stroke={LINE_COLORS[index % LINE_COLORS.length]}
                                                        strokeWidth={3}
                                                        dot={{r: 4}}
                                                        activeDot={{r: 6}}
                                                    />
                                                ))}
                                            </LineChartA>
                                        </ResponsiveContainerA>
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <p>Nenhum dado de teste encontrado para esta turma.</p>
                                        </div>
                                    )}
                                </div>
                                
                                <div style={{marginTop: '20px', padding: '0 20px', color: '#666', fontSize: '0.9rem'}}>
                                    * As notas exibidas representam a média das 4 competências (Abstração, Decomposição, Padrões, Algoritmos) em cada teste realizado.
                                </div>
                            </>
                        ) : (
                            <div className={styles.emptyState}>
                                <p>Selecione uma turma para ver o gráfico.</p>
                            </div>
                        )}
                    </main>
                </div>
            </Board>
        </div>
    );
};